import type { ReactNode } from "react";
import { RotateCcw } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { Button } from "../../components/ui/button.js";

export interface StatusFilterOption {
  /** "" is the "All" chip. */
  value: string;
  label: string;
  /** What the chip carries: a headcount, or a formatted figure such as a money total. */
  count: ReactNode;
  /** Tailwind class painting the status dot from its reserved token. */
  swatchClass: string;
}

export interface StatusFilterChipsProps {
  options: StatusFilterOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /**
   * Clears every filter the row's owner holds, not just the status chip -- on the
   * Employees page that includes the search box and the department, office and type
   * pickers. Omit it to leave the row without a Reset.
   */
  onReset?: (() => void) | undefined;
  /** Nothing is filtered, so there is nothing to reset. The button stays in place
   *  and greys out rather than appearing and disappearing under the cursor. */
  resetDisabled?: boolean;
}

/**
 * One row of status filter chips, each carrying how many rows it would select.
 * The counts are facet counts — they stay on the other filters, so a chip never
 * reads 0 for a selection that would actually return rows.
 */
export function StatusFilterChips({
  options, value, onChange, className, onReset, resetDisabled = false,
}: StatusFilterChipsProps) {
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)} role="group" aria-label="Filter by status">
      {options.map(o => {
        const selected = value === o.value;
        return (
          <Button
            key={o.value || "all"}
            type="button"
            size="sm"
            variant={selected ? "default" : "secondary"}
            aria-pressed={selected}
            onClick={() => onChange(selected && o.value !== "" ? "" : o.value)}
            className="gap-1.5"
          >
            {o.value !== "" && <span className={cn("size-1.5 shrink-0 rounded-full", o.swatchClass)} aria-hidden />}
            {o.label}
            <span className={cn("tabular-nums", selected ? "text-white/70" : "text-surface-400")}>{o.count}</span>
          </Button>
        );
      })}

      {onReset && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onReset}
          disabled={resetDisabled}
          className="ml-auto gap-1.5 text-surface-600"
        >
          <RotateCcw className="size-3.5 shrink-0" aria-hidden />
          Reset
        </Button>
      )}
    </div>
  );
}
